// Domain · ProductChip
//
// Accepts a `ProductJoin` (the FK-relation shape Supabase returns) and
// renders the canonical product affordance — an EntityLink in chip or
// inline variant. Owns the null + missing-label fallback so callers stop
// writing `r.product?.id ?? r.product_id ?? 0` ladders in their `data`
// mappers.
//
// Variants mirror `EntityChip` / `EntityLink`:
//   - `"chip"`   — full chip chrome (icon · label · arrow).
//   - `"inline"` (default) — link-styled text with a trailing arrow.

import React from "react";
import { withExportSpec } from "@ds/display/exportSpec";
import { absoluteAppUrl, getEntityRoute } from "@ds/navigation/navigation";
import type { ProductJoin } from "@shared/types/product";
import { useEntityLinkRenderer } from "@ds/display/entityLinkRendererContext";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface ProductChipInput {
  /**
   * Joined product row from Supabase (`r.product`).
   * `null` / `undefined` / missing id renders an em-dash.
   */
  product: ProductJoin | null | undefined;
  /** Rendering variant. Default `"inline"` for table density. */
  variant?: "chip" | "inline";
  /** Size scale. Default `md`. Only applies when `variant="chip"`. */
  size?: "sm" | "md";
  /** `data-testid` on the root. Defaults to `"product-chip"`. */
  testId?: string;
}

export type ProductChipProps = ProductChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function ProductChip({
  product,
  variant = "inline",
  size,
  testId = "product-chip",
}: ProductChipProps) {
  const renderLink = useEntityLinkRenderer();
  if (!product?.id) {
    return <span data-testid={testId}>—</span>;
  }
  return renderLink({
    type: "product",
    id: product.id,
    label: product.name ?? `#${product.id}`,
    variant: variant === "chip" ? "pill" : "inline",
    size,
    testId,
  }) as React.ReactElement;
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// See CompanyChip — derivation reads the declared component, so the chip that
// renders the EntityLink has to carry the tag itself. Unlike CompanyChip this
// one has no plain-text branch: without an id it paints an em-dash, so the
// export declines the cell and lets the shadow text carry that dash through.

withExportSpec<typeof ProductChip, ProductChipProps>(ProductChip, {
  type: "link",
  value: (p) => {
    const id = p.product?.id;
    if (!id) return undefined;
    return p.product?.name || `#${id}`;
  },
  hyperlink: (p) =>
    p.product?.id ? absoluteAppUrl(getEntityRoute("product", p.product.id)) : undefined,
});
